import { useEffect, useState } from "react";
import styles from "./dashboard.module.scss";
import { connect } from "react-redux";
import {
  getPOMDashboardSummaryRequest,
  getPOMDashboardDetailRequest,
  getPOMDashboardGroupedRequest,
  clearPOMDashboardData,
} from "../../store/actions/general.actions";
import {
  pomDashboardSummary,
  pomDashboardDetail,
  pomDashboardLoading,
  pomDashboardDetailLoading,
  pomDashboardGrouped,
  pomDashboardGroupedLoading,
} from "../../store/selectors/general.selector";
import Portfolios from "../portfolio/portfolios";
import RiskCard from "./components/PortfolioManagerDashboard/RiskCard";
import ProgressCard from "./components/PortfolioManagerDashboard/ProgressCard";
import SimpleCard from "./components/PortfolioManagerDashboard/SimpleCard";
import HealthBadgeCard from "./components/PortfolioManagerDashboard/HealthBadgeCard";
import PortfolioValuePanel from "./components/PortfolioManagerDashboard/PortfolioValuePanel";
import ResourceCapacityPanel from "./components/PortfolioManagerDashboard/ResourceCapacityPanel";
import { RiskMap } from "../../components/charts/DashboardCharts";
import pmStyles from "./styles/portfolioManagerDashboard.module.scss";
import Programs from "../program/programs";
import BudgetCostCard from "./components/PortfolioManagerDashboard/CostsCard";
import { DashboardSkeleton } from "./components/PortfolioManagerDashboard/DashboardSkeletons";
import PortfolioProjectsDonut from "./components/PortfolioManagerDashboard/PortfolioProjectsDonut";
import PortfoliosPanel from "./components/PortfolioManagerDashboard/PortfoliosPanel";
import GroupedKpiCards from "./components/PortfolioManagerDashboard/GroupedKpiCards";
import GroupComparisonChart from "./components/PortfolioManagerDashboard/GroupComparisonChart";
import DashboardFilterBar from "./components/PortfolioManagerDashboard/DashboardFilterBar";
import { buildStatusSegments, buildRiskSeverityRows, KPI_INFO } from "./components/PortfolioManagerDashboard/kpiHelpers";
import KpiInfoIcon from "./components/PortfolioManagerDashboard/KpiInfoIcon";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function buildCapacityData(rc) {
  if (!rc) return [];
  const { planned = [], logged = [] } = rc;
  return MONTH_NAMES.map((name, i) => {
    const m = i + 1;
    return {
      month: name,
      planned: planned.find((d) => d.month === m)?.value ?? 0,
      actual: logged.find((d) => d.month === m)?.value ?? 0,
    };
  });
}


function buildValueChartData(valueDelivered = [], budgetBurnRate = []) {
  return MONTH_NAMES.map((name, i) => {
    const m = i + 1;
    const vd = valueDelivered.find((d) => d.month === m);
    const br = budgetBurnRate.find((d) => d.month === m);
    return {
      label: name,
      planned: vd?.planned ?? 0,
      actual: vd?.actual ?? 0,
      burnRate: br?.burnRatePercentage ?? 0,
      monthlySpend: br?.monthlySpend ?? 0,
    };
  });
}

const PortfolioManagerDashboard = (props) => {
  const {
    PortfolioId,
    setPortfolioId,
    setPortfolioName,
    filters: externalFilters,
    getPOMDashboardSummaryRequest,
    getPOMDashboardDetailRequest,
    getPOMDashboardGroupedRequest,
    clearPOMDashboardData,
    summary,
    isLoading,
    groupedData,
    groupedLoading,
  } = props;

  // filters is normally owned by DashboardHeader (the "Filters" button next to the tabs) so it
  // survives this component's own loading/unmount cycles. Overview.jsx's fixed-portfolio Dashboard
  // tab has no such header to attach a button to, so it doesn't pass filters at all - fall back to
  // owning the filter bar (and its state) locally in that case.
  const [internalFilters, setInternalFilters] = useState({});
  const ownsFilterBar = externalFilters === undefined;
  const filters = ownsFilterBar ? internalFilters : externalFilters;
  const [selectedGroup, setSelectedGroup] = useState(null);

  useEffect(() => {
    if (PortfolioId) {
      clearPOMDashboardData();
      getPOMDashboardDetailRequest(PortfolioId, filters);
    } else {
      getPOMDashboardSummaryRequest(filters);
    }
    // Grouping is available in both modes - within a single portfolio it's scoped to just that
    // portfolio's projects (grouped by Program/Industry/Project), same request shape either way.
    if (filters.groupBy && filters.groupBy !== "None") {
      getPOMDashboardGroupedRequest(filters, PortfolioId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PortfolioId, filters]);

  // A group selected under one filter/grouping combination has no meaning under the next one -
  // drop it whenever the filters (or the grouping dimension itself) change.
  useEffect(() => {
    setSelectedGroup(null);
  }, [filters]);

  const isGrouped = filters.groupBy && filters.groupBy !== "None";

  return (
    <>
      <div className={`mt-4 ${styles.SummaryPanel}`}>
        {ownsFilterBar && (
          <div className="mb-3">
            <DashboardFilterBar mode={PortfolioId ? "detail" : "summary"} onApply={setInternalFilters} />
          </div>
        )}
        {isLoading ? (
          <DashboardSkeleton />
        ) : (
        <>
        <div className="flex gap-3 w-full mb-3 ">
          <RiskCard
            value={summary?.projectsAtRisk?.current ?? 0}
            trend={(summary?.projectsAtRisk?.current ?? 0) - (summary?.projectsAtRisk?.previous ?? 0)}
            trendLabel="vs last period"
            infoText={KPI_INFO.atRisk}
          />
          <HealthBadgeCard
            current={summary?.health?.current ?? 0}
            trending={summary?.health?.trending ?? 0}
            infoText={KPI_INFO.health}
          />
          <ProgressCard
            title="Overall Progress"
            value={summary?.overallProgress ?? 0}
            infoText={KPI_INFO.overallProgress}
          />
          <SimpleCard
            title="Active Projects"
            value={summary?.activeProjects ?? 0}
            secondaryValue={summary?.totalProjects ?? 0}
            infoText={KPI_INFO.activeProjects}
          />
          <SimpleCard
            title="On-Time Delivery"
            value={`${Math.round((summary?.onTimeDelivery ?? 0) * 100)}%`}
            infoText={KPI_INFO.onTimeDelivery}
          />
          <SimpleCard
            title="Gross Margin"
            value={`${summary?.costsSummary?.grossMarginPercentage ?? 0}%`}
            infoText={KPI_INFO.grossMargin}
          />
          <BudgetCostCard
            budgetChartData={summary?.costsSummary}
            infoText={KPI_INFO.budget}
          />
        </div>
        {isGrouped && !groupedLoading && (
          <div className="row mb-3">
            <div className="col-12">
              <GroupComparisonChart
                groups={groupedData}
                selectedGroup={selectedGroup}
                onSelectGroup={setSelectedGroup}
              />
            </div>
          </div>
        )}
        {isGrouped && (
          <div className="row mb-3">
            <div className="col-12">
              <GroupedKpiCards
                groups={groupedData}
                loading={groupedLoading}
                groupByLabel={filters.groupBy}
                selectedGroup={selectedGroup}
                onSelectGroup={setSelectedGroup}
              />
            </div>
          </div>
        )}
        {!PortfolioId && !isGrouped && summary?.flaggedPortfolios?.length > 0 && (
          <div className="row mb-3">
            <div className="col-12">
              <PortfoliosPanel
                portfolios={summary.flaggedPortfolios}
                onPortfolioClick={(portfolio) => {
                  setPortfolioId(portfolio.id);
                  setPortfolioName(portfolio.name);
                }}
                loading={isLoading}
              />
            </div>
          </div>
        )}
        <div className="row align-items-stretch">
          <div className="col-6 mb-3">
            <PortfolioValuePanel
              gaugeSegments={buildStatusSegments(summary?.projectsByStatus)}
              chartData={buildValueChartData(summary?.valueDelivered, summary?.budgetBurnRate)}
            />
          </div>
          <div className="col-3 mb-3">
            <PortfolioProjectsDonut
              gaugeSegments={buildStatusSegments(summary?.projectsByStatus)}
            />
          </div>
          <div className="col-3 mb-3">
            <div className={pmStyles.analyticsPanel}>
              <KpiInfoIcon content={KPI_INFO.risk} />
              <div className={pmStyles.panelHeader}>
                <span className={pmStyles.label}>Risk Heat Map</span>
              </div>
              <div style={{ position: "relative", width: "100%", height: "260px" }}>
                <RiskMap riskData={summary?.risks ?? []} />
              </div>
              <div className={pmStyles.pvRiskRows}>
                {buildRiskSeverityRows(summary?.risksBySeverity).map((row) => (
                  <div className={pmStyles.pvRiskRow} key={row.label}>
                    <span className={pmStyles.pvRiskDot} style={{ background: row.color }} />
                    <span className={pmStyles.pvRiskLabel}>{row.label}</span>
                    <div className={pmStyles.pvRiskBarTrack}>
                      <div className={pmStyles.pvRiskBarFill} style={{ width: `${row.pct}%`, background: row.color }} />
                    </div>
                    <span className={pmStyles.pvRiskPct}>{row.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="row mb-3">
          <div className="col-12 mb-3 d-flex flex-column" style={{ height: "400px" }}>
            <ResourceCapacityPanel
              data={buildCapacityData(summary?.resourceCapacity)}
            />
          </div>
          {/* <div className="col-7 mb-3 portfolio-table" style={{ height: "400px", overflow: "auto" }}>
            {PortfolioId ? (
              <Programs
                portfolioID={PortfolioId}
                overviewType="Dashboard"
              />
            ) : (
              <Portfolios
                overviewType="Dashboard"
                dashboardFilterSetId={setPortfolioId}
                dashboardFilterSetName={setPortfolioName}
                loadingSkeleton={isLoading}
                dashboardFilterId={PortfolioId}
              />
            )}
          </div> */}
        </div>
      </>
        )}
      </div>
    </>
  );
};

const mapDispatchToProps = {
  getPOMDashboardSummaryRequest,
  getPOMDashboardDetailRequest,
  getPOMDashboardGroupedRequest,
  clearPOMDashboardData,
};

const mapStateToProps = (state, ownProps) => {
  const toPlain = (val) => (val?.toJS ? val.toJS() : val) ?? {};
  const summary = toPlain(pomDashboardSummary(state));
  const detail = toPlain(pomDashboardDetail(state));
  const grouped = pomDashboardGrouped(state);
  const groupedData = grouped?.toJS ? grouped.toJS() : grouped ?? [];
  const groupedLoading = pomDashboardGroupedLoading(state);
  const fetchLoading = ownProps.PortfolioId
    ? pomDashboardDetailLoading(state)
    : pomDashboardLoading(state);

  if (ownProps.PortfolioId) {
    const resolvedSummary = Object.keys(detail).length > 0 ? detail : summary;
    const isLoading = fetchLoading || Object.keys(resolvedSummary).length === 0;
    return { isLoading, summary: resolvedSummary, groupedData, groupedLoading };
  } else {
    const isLoading = fetchLoading || Object.keys(summary).length === 0;
    return { isLoading, summary, groupedData, groupedLoading };
  }
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PortfolioManagerDashboard);
